import {useInitData} from "@tma.js/sdk-react";
import {
    FETCH_ERROR,
    getUser,
    LOADED,
    NOT_FOUND,
    postAuth, setResetAuthLayer,
    setToken
} from "../../../communication/communication";
import React, {useEffect, useRef, useState} from "react";
import {RegisterProps, RegisterView} from "./RegisterView";
import './AuthLayer.css'
import {useInitConfig} from "../../../communication/configuration";
import {LogInProgress} from "./LogInProgress";
import {useTranslation} from "react-i18next";

enum AuthStage {
    Error,
    Started,
    GotToken,
    GotConfig,
    UserNotRegistered,
    LoggedIn
}

type AuthStatus = {
    stage : AuthStage
    errorMessage : string | null
}

function authOk(stage : AuthStage) : AuthStatus {
    return { stage : stage, errorMessage : null };
}

function authErr(error : string) : AuthStatus {
    return { stage : AuthStage.Error, errorMessage : error };
}

function showElement(error: JSX.Element) {
    return <>
        <div className="full-size flex-center">
            {error}
        </div><div/><div/>
        <div className="bg-black"/>
    </>
}

function showError(error : string){
    const elem = <span className="font-white x-center"> {error} </span>
    return showElement(elem)
}

type AuthProps = {
    authState : AuthStatus,
    setAuthState : (newStatus : AuthStatus) => void
    children: JSX.Element
}


const AuthLayerInner = React.memo(function AuthLayer(props : AuthProps){
    const initData = useInitData()
    const [authState, setAuthState] = [props.authState, props.setAuthState];
    const shouldRunLogic = useRef(true)
    const shouldRequestAuth = useRef(true)
    const {t} = useTranslation()
    
    const updateJWT = () => {
        if (!shouldRequestAuth.current) return Promise.resolve();
        shouldRequestAuth.current = false;
        const hashEncoded = window.location.hash.slice(1);
        const tgWebAppData = new URLSearchParams(hashEncoded).get("tgWebAppData");
        const payload = {init_data: tgWebAppData};
        return postAuth(JSON.stringify(payload)).then(r => {
            if (r.statusCode === LOADED) {
                setToken(r.data!.token_id!);
                // If it is refresh call, don't change state
                if (!shouldRunLogic.current) return;
                setAuthState(authOk(AuthStage.GotToken));
            }
            else if (r.statusCode === FETCH_ERROR){
                shouldRunLogic.current = true;
                setAuthState(authErr(t("checkYourInternetConnection")));
            } 
            else {
                shouldRunLogic.current = true;
                setAuthState(authErr(`${r.data}`));
            }
        });
    }

    const [configs, initConfigs] = useInitConfig()

    useEffect(() => {
        if (authState.stage !== AuthStage.GotToken) return;

        initConfigs();

        if (configs.statusCode === LOADED) {
            setAuthState(authOk(AuthStage.GotConfig));
            return;
        }

        else if (configs.statusCode === FETCH_ERROR) {
            shouldRunLogic.current = true;
            setAuthState(authErr(t("checkYourInternetConnection")));
        }
    }, [authState.stage, configs, initConfigs, setAuthState, t])

    // To call from
    setResetAuthLayer(() => { shouldRequestAuth.current = true; return updateJWT()});
    
    const checkUserExists = () => {
        if (initData === undefined || initData.user === undefined) {
            setAuthState(authErr(t("initDataWasNotDefined")));
            return;
        }
        
        getUser().then(r => {
            if (r.statusCode === LOADED) setAuthState(authOk(AuthStage.LoggedIn));
            else if (r.statusCode === NOT_FOUND) setAuthState(authOk(AuthStage.UserNotRegistered));
            else if (r.statusCode === FETCH_ERROR) setAuthState(authErr(t("requestFailedCheckYourInternetConnection")));
            else setAuthState(authErr(t("errorWhileCheckingUser") + ": " + r.statusCode.toString()));
        });
    }

    const registerProps : RegisterProps = {
        register: () => {setAuthState(authOk(AuthStage.LoggedIn))},
        initData: initData,
        // TODO: validate
        user: initData!.user!.username ? initData!.user!.username : initData!.user!.firstName
    }

    if (authState.stage === AuthStage.Error) {
        return showError(authState.errorMessage!)
    }

    if (authState.stage === AuthStage.Started) {
        updateJWT()
        return <LogInProgress progress={1} data={t("loggingIn")}/>
    }

    if (authState.stage === AuthStage.GotToken) {
        return <LogInProgress progress={2} data={t("gettingAppData")}/>
    }

    if (authState.stage === AuthStage.GotConfig) {
        checkUserExists()
        return <LogInProgress progress={4} data={t("gettingUserData")}/>
    }

    if (authState.stage === AuthStage.UserNotRegistered) {
        return <RegisterView props={registerProps}/>
    }

    if (authState.stage === AuthStage.LoggedIn) {
        shouldRunLogic.current = false;
        return props.children;
    }
    
    return showError(t("illegalState"))
}, (prev, curr) => (prev.authState.stage === curr.authState.stage));

export function AuthLayer({children} : {children : JSX.Element}) {
    const [authState, setAuthState] = useState(authOk(AuthStage.Started));
    const props : AuthProps = {
        authState: authState,
        setAuthState: setAuthState,
        children: children
    }
    return <AuthLayerInner {...props}/>
}